<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '../../api/client';
  import { TRIP_KINDS, type AlertRule, type AlertSetting, type Member, type MyAlertSetting, type Place } from '../../api/types';
  import { currentUser } from '../../stores/auth';
  import { confirm } from '../../stores/confirmDialog';
  import PageHeader from '../PageHeader.svelte';
  import AlertEditor from './AlertEditor.svelte';
  import { t } from '../../i18n/index.ts';

  // GroupAlerts is the viewer's alerts about one group's members, on the
  // group's page (spec 003 FR-011): their setting for everyone first, then
  // each member's own, each with Edit and Turn off or Remove, and a way to
  // start one for a member. Past alerts are not listed; they arrive as
  // notifications. The page hands over the group's members, which it keeps
  // current (001 FR-112).
  let { groupId, groupName, members }: { groupId: string; groupName: string; members: Member[] } = $props();

  // Editing is the setting open in the editor; a new member setting has no
  // stored counterpart yet.
  interface Editing {
    subject: string;
    setting: AlertSetting;
    isNew: boolean;
  }

  let settings: MyAlertSetting[] = $state([]);
  let places: Place[] = $state([]);
  let editing: Editing | null = $state(null);
  let error = $state('');
  let addMember = $state('');

  let addable = $derived(members.filter((m) => m.user_id !== $currentUser?.id && !settings.some((s) => s.subject_id === m.user_id)));

  $effect(() => {
    const id = groupId;
    editing = null;
    // A reply for a group no longer shown is dropped, so a slow one cannot
    // fill in another group's places.
    api.get<Place[]>(`/groups/${id}/places`)
      .then((p) => { if (id === groupId) places = p; })
      .catch((e: any) => { if (id === groupId) error = e.message; });
  });

  // The alerts are read again whenever the page's members change, since a
  // member who leaves the group takes the alerts about them along.
  $effect(() => {
    const id = groupId;
    members;
    untrack(() => load(id).catch((e: any) => { if (id === groupId) error = e.message; }));
  });

  async function load(id = groupId) {
    const all = await api.get<MyAlertSetting[]>('/me/alerts');
    if (id !== groupId) return;
    settings = all.filter((s) => s.group_id === id).sort((a, b) => (a.subject_id ? 1 : 0) - (b.subject_id ? 1 : 0));
    error = '';
  }

  function rule(r: AlertRule): string {
    const what = r.on_arrive && r.on_depart ? 'both' : r.on_arrive ? 'arrive' : 'leave';
    return r.place_name ? t('alertsRuleAt', { what, place: r.place_name }) : t('alertsRuleAnywhere', { what });
  }

  // Every kind chosen reads as one line rather than five.
  function tripEnds(s: AlertSetting): string[] {
    if (TRIP_KINDS.every((k) => s.trip_ends.includes(k))) return [t('alertsFinishes', { kind: 'any' })];
    return TRIP_KINDS.filter((k) => s.trip_ends.includes(k)).map((kind) => t('alertsFinishes', { kind }));
  }

  // startMember opens a new setting for a member, starting from the group's
  // everyone setting, since the new one then decides for them.
  function startMember() {
    if (!addMember) return;
    const every = settings.find((s) => !s.subject_id);
    editing = { subject: addMember, setting: { subject_id: addMember, trip_ends: every?.trip_ends ?? [], phone: every?.phone ?? false, places: every?.places ?? [] }, isNew: true };
    addMember = '';
  }

  async function remove(s: MyAlertSetting) {
    const ok = await confirm(
      s.subject_id
        ? t('alertsRemoveMemberConfirm', { member: s.subject_name, group: groupName })
        : t('alertsTurnOffEveryoneConfirm', { group: groupName }),
    );
    if (!ok) return;
    try {
      await api.delete(`/groups/${groupId}/alerts/${s.subject_id ?? 'everyone'}`);
      await load();
    } catch (e: any) {
      error = e.message;
    }
  }

  async function saved() {
    editing = null;
    try {
      await load();
    } catch (e: any) {
      error = e.message;
    }
  }
</script>

<div class="card p-5 mb-6 space-y-3">
  <PageHeader title={t('alertsTitle')} level="section" />
  <p class="text-sm text-text-muted">{t('alertsIntro', { group: groupName })}</p>
  {#if error}
    <p class="text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}

  {#if editing?.isNew}
    <div class="border rounded p-3 space-y-2" style="border-color: var(--theme-border);">
      <p class="font-semibold">{members.find((m) => m.user_id === editing!.subject)?.display_name}</p>
      <p class="text-sm text-text-muted">{t('alertsStartsFromEveryone', { group: groupName })}</p>
      <AlertEditor {groupId} subject={editing.subject} setting={editing.setting} {places} onSaved={saved} onCancel={() => (editing = null)} />
    </div>
  {/if}

  {#each settings as s (s.subject_id ?? 'everyone')}
    <div class="border rounded p-3 space-y-2" style="border-color: var(--theme-border);">
      <p class="font-semibold text-sm">{s.subject_name ?? t('alertsEveryone')}</p>
      {#if editing && !editing.isNew && editing.subject === (s.subject_id ?? 'everyone')}
        <AlertEditor {groupId} subject={editing.subject} setting={s} {places} onSaved={saved} onCancel={() => (editing = null)} />
      {:else}
        <div class="flex justify-between gap-2 text-sm">
          <ul class="space-y-0.5">
            {#each tripEnds(s) as line (line)}
              <li><i aria-hidden="true" class="las la-flag-checkered mr-1"></i>{line}</li>
            {/each}
            {#each s.places as r (r.id)}
              <li><i aria-hidden="true" class="las la-bell mr-1"></i>{rule(r)}</li>
            {/each}
            {#if s.phone}
              <li><i aria-hidden="true" class="las la-battery-quarter mr-1"></i>{t('alertsPhoneShort')}</li>
            {/if}
            {#if s.trip_ends.length === 0 && s.places.length === 0 && !s.phone}
              <li class="text-text-muted">{t('alertsNoAlerts')}</li>
            {/if}
          </ul>
          <div class="flex gap-2 shrink-0 items-start">
            <button class="btn btn-secondary btn-sm" onclick={() => (editing = { subject: s.subject_id ?? 'everyone', setting: s, isNew: false })}>{t('appEdit')}</button>
            {#if s.subject_id || s.trip_ends.length > 0 || s.places.length > 0 || s.phone}
              <button class="btn btn-secondary btn-sm" onclick={() => remove(s)}>{s.subject_id ? t('appRemove') : t('alertsTurnOff')}</button>
            {/if}
          </div>
        </div>
      {/if}
    </div>
  {/each}

  {#if !editing && addable.length > 0}
    <div class="flex flex-wrap items-center gap-2 text-sm pt-1">
      <span>{t('alertsForOneMember')}</span>
      <select class="input py-1" bind:value={addMember} aria-label={t('alertsMember')}>
        <option value="">{t('alertsChooseMember')}</option>
        {#each addable as m (m.user_id)}
          <option value={m.user_id}>{m.display_name}</option>
        {/each}
      </select>
      <button class="btn btn-secondary btn-sm" disabled={!addMember} onclick={startMember}>{t('alertsSetUp')}</button>
    </div>
  {/if}
</div>
